import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";

// Force a stored embed string to fill its container (override width/height
// attributes on the top-level <iframe> without editing the stored HTML).
// In the tile: pointer-events disabled on the iframe so clicks bubble to
// the Card and open the enlarge modal (per plan). In the modal the iframe
// gets pointer-events back so the user can interact with the live stream.
const embedStyleBlock = `
  .liveview-embed { position: relative; width: 100%; }
  .liveview-embed > iframe {
    width: 100% !important;
    height: 100% !important;
    display: block;
    border: 0;
  }
  .liveview-embed-tile > iframe {
    pointer-events: none;
  }
`;

// One tile in the grid. Rendered as a plain div — the enlarge action is
// wired on the whole tile except the reload button.
const LiveViewTile = ({ entry, index, onEnlarge, onReload, reloadKey, t, tileHeight }) => {
  const html = entry.embed || '';
  return (
    <Card
      className="panel lightCard"
      style={{
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s',
        overflow: 'hidden',
      }}
      onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'; }}
      onMouseOut={(e) => { e.currentTarget.style.boxShadow = ''; }}
      onClick={onEnlarge}
      data-testid={`liveview-tile-${index}`}
    >
      <CardContent style={{ padding: 12 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          gap: 8,
        }}>
          <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.name || `Liveview ${index + 1}`}
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button
              type="button"
              title={t('live_view_reload')}
              onClick={(e) => { e.stopPropagation(); onReload(); }}
              style={{
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 14,
              }}
              data-testid={`liveview-reload-${index}`}
            >
              🔄
            </button>
            <button
              type="button"
              title={t('live_view_enlarge')}
              onClick={(e) => { e.stopPropagation(); onEnlarge(); }}
              style={{
                border: '1px solid #e5e7eb',
                background: '#ffffff',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 14,
              }}
              data-testid={`liveview-enlarge-${index}`}
            >
              ⛶
            </button>
          </div>
        </div>
        <div
          className="liveview-embed liveview-embed-tile"
          style={{ height: tileHeight, background: '#0f172a' }}
          key={reloadKey}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </CardContent>
    </Card>
  );
};

const EnlargeModal = ({ entry, onClose, t }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2vh 2vw',
      }}
      data-testid="liveview-modal"
    >
      <div style={{
        width: '96vw',
        height: '96vh',
        maxWidth: '96vw',
        maxHeight: '96vh',
        background: '#0f172a',
        borderRadius: 8,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          position: 'absolute',
          top: 12,
          left: 16,
          color: '#f9fafb',
          fontWeight: 600,
          fontSize: 16,
          background: 'rgba(0,0,0,0.35)',
          padding: '4px 10px',
          borderRadius: 6,
          zIndex: 2,
          maxWidth: '80%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {entry.name}
        </div>
        <button
          type="button"
          onClick={onClose}
          title={t('live_view_close')}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            border: 0,
            background: 'rgba(0,0,0,0.55)',
            color: '#ffffff',
            width: 40,
            height: 40,
            borderRadius: 20,
            fontSize: 22,
            cursor: 'pointer',
            lineHeight: '40px',
            padding: 0,
          }}
          data-testid="liveview-modal-close"
        >
          ✕
        </button>
        <div
          className="liveview-embed"
          style={{ width: '100%', height: '100%' }}
          dangerouslySetInnerHTML={{ __html: entry.embed || '' }}
        />
      </div>
    </div>
  );
};

const LiveViewModule = ({ t, isMobile, liveViews, setActiveTab }) => {
  const [enlarged, setEnlarged] = useState(null); // entry currently in modal
  // per-tile reload counters — bumping this remounts the iframe host div
  const [reloadKeys, setReloadKeys] = useState({});
  const entries = Array.isArray(liveViews) ? liveViews : [];

  const bumpReload = useCallback((idx) => {
    setReloadKeys((prev) => ({ ...prev, [idx]: (prev[idx] || 0) + 1 }));
  }, []);

  const tileHeight = isMobile ? 240 : 340;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      padding: isMobile ? '0 8px' : 0,
      maxWidth: 1400,
      margin: '0 auto',
    }}>
      <style>{embedStyleBlock}</style>

      <Card className="panel lightCard" style={{ marginTop: 8 }}>
        <CardContent>
          <div className="panel-title darktxt" style={{ fontSize: isMobile ? '18px' : '20px' }}>
            📺 {t('live_view_title')}
          </div>
          <div className="small" style={{ color: '#6b7280', fontSize: isMobile ? '13px' : '14px' }}>
            {t('live_view_subtitle')}
          </div>
        </CardContent>
      </Card>

      {entries.length === 0 ? (
        <Card className="panel lightCard">
          <CardContent>
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{ fontSize: 42, marginBottom: 12 }}>📺</div>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>
                {t('live_view_empty_title')}
              </div>
              <div className="small" style={{ color: '#6b7280', marginBottom: 16 }}>
                {t('live_view_empty_hint')}
              </div>
              <Button
                variant="outline"
                onClick={() => setActiveTab('settings')}
                data-testid="liveview-empty-goto-settings"
              >
                ⚙️ {t('live_view_empty_cta')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
          }}
          data-testid="liveview-grid"
        >
          {entries.map((entry, idx) => (
            <LiveViewTile
              key={idx}
              entry={entry}
              index={idx}
              tileHeight={tileHeight}
              reloadKey={reloadKeys[idx] || 0}
              onEnlarge={() => setEnlarged({ ...entry, __idx: idx })}
              onReload={() => bumpReload(idx)}
              t={t}
            />
          ))}
        </div>
      )}

      {enlarged && (
        <EnlargeModal
          entry={enlarged}
          onClose={() => setEnlarged(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default LiveViewModule;
